import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { computeProjectPaidSoFar, computeSettlementPayment, fullName } from "@torre/team";
import { useTheme } from "../../../context/ThemeContext";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import {
  buildThemeClasses,
  formatMoney,
  settlementQuantity,
  modeLabel,
  MODE_TONES,
  SUBJECT_OPTIONS,
  subjectLabel,
  SETTLEMENT_STATUS_OPTIONS,
  settlementStatusLabel,
  SETTLEMENT_STATUS_TONES,
} from "../theme";
import {
  fetchSettlements,
  createSettlement,
  updateSettlement,
  deleteSettlement,
  fetchPayRates,
  fetchTeamMembers,
  fetchSquads,
} from "../services/teamService";
import type { Squad, WorkSettlement, PayRate, TeamMember } from "../types";
import { useIsStaffAdmin } from "../../../hooks/useIsStaffAdmin";

// Pago a entregable/día (AgroCloud#13): registrar una tarea y que el sistema calcule el
// pago según entregables y tarifa, imputando el costo al proyecto/centro de
// costo. El total se previsualiza en vivo en el formulario con la misma función
// pura (`computeSettlementPayment` de @torre/team) que el backend usa al
// persistir, así que la vista previa y el documento guardado nunca divergen.
export default function SettlementsSection() {
  // #1007: solo quien administra modifica; los demás ven lo suyo en solo lectura.
  const isStaffAdmin = useIsStaffAdmin();
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [rows, setRows] = useState<Row[]>([]);
  const [rates, setRates] = useState<PayRate[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [squads, setSquads] = useState<Squad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settlements, rates, membersPage, squadsPage] = await Promise.all([
        fetchSettlements(),
        fetchPayRates(),
        fetchTeamMembers(),
        fetchSquads(),
      ]);
      setRates(rates.items);
      setTeamMembers(membersPage.items);
      setSquads(squadsPage.items);
      setRows(settlements.items as unknown as Row[]);
    } catch {
      setError(t("team.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const rateOptions = useMemo(
    () =>
      rates.map((x) => ({
        value: x.id,
        label: `${x.name} · ${modeLabel(t, x.mode ?? "deliverable")}`,
      })),
    [rates, t],
  );

  const columns: Column[] = [
    {
      key: "subjectName",
      label: t("team.fields.subject"),
      render: (r) => String(r.teamMemberName ?? r.squadName ?? "—"),
    },
    { key: "payRateName", label: t("team.fields.work") },
    { key: "date", label: t("team.fields.date") },
    {
      key: "mode",
      label: t("team.fields.mode"),
      render: (r) => (
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] ${
            MODE_TONES[String(r.mode)] ?? "bg-gray-400/15 text-gray-500"
          }`}
        >
          {modeLabel(t, r.mode)}
        </span>
      ),
    },
    {
      key: "output",
      label: t("team.fields.outputDays"),
      align: "right",
      render: (r) => settlementQuantity(r),
    },
    {
      key: "appliedUnitPrice",
      label: t("team.fields.appliedPrice"),
      align: "right",
      // Sin precio aplicado NO es precio 0: `Number(null)` sería un cero falso.
      render: (r) => formatMoney(r.appliedUnitPrice),
    },
    {
      key: "bonus",
      label: t("team.fields.bonus"),
      align: "right",
      render: (r) => formatMoney(r.bonus),
    },
    {
      key: "total",
      label: t("team.fields.total"),
      align: "right",
      render: (r) => (
        <span className="font-semibold text-primary-500">{formatMoney(r.total)}</span>
      ),
    },
    {
      key: "projectId",
      label: t("team.fields.project"),
      render: (r) => String(r.projectId ?? "—"),
    },
    {
      key: "status",
      label: t("team.fields.status"),
      align: "center",
      render: (r) => (
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] ${
            SETTLEMENT_STATUS_TONES[String(r.status)] ?? "bg-gray-400/15 text-gray-500"
          }`}
        >
          {settlementStatusLabel(t, r.status)}
        </span>
      ),
    },
  ];

  const modeOf = (v: Record<string, string>) =>
    rates.find((x) => x.id === v.payRateId)?.mode ?? "deliverable";
  const whenMode = (...modes: string[]) => (v: Record<string, string>) => modes.includes(modeOf(v));

  const fields: FormField[] = [
    {
      key: "subject",
      label: t("team.fields.subjectType"),
      type: "select",
      required: true,
      defaultValue: "team_member",
      options: SUBJECT_OPTIONS.map((s) => ({ value: s, label: subjectLabel(t, s) })),
      hint: t("team.hints.subject"),
    },
    {
      key: "teamMemberId",
      label: t("team.fields.teamMember"),
      type: "select",
      options: teamMembers.map((w) => ({ value: w.id, label: fullName(w) })),
    },
    {
      key: "squadId",
      label: t("team.fields.squad"),
      type: "select",
      options: squads.map((c) => ({ value: c.id, label: c.name })),
    },
    {
      key: "payRateId",
      label: t("team.fields.work"),
      type: "select",
      required: true,
      options: rateOptions,
    },
    { key: "date", label: t("team.fields.date"), type: "date" },
    {
      key: "output",
      label: t("team.fields.output"),
      type: "number",
      hint: t("team.hints.output"),
      visibleWhen: whenMode("deliverable", "hourly", "daily"),
    },
    {
      key: "days",
      label: t("team.fields.days"),
      type: "number",
      hint: t("team.hints.days"),
      visibleWhen: whenMode("daily"),
    },
    {
      key: "hours",
      label: t("team.fields.hours"),
      type: "number",
      hint: t("team.hints.hours"),
      visibleWhen: whenMode("hourly"),
    },
    {
      key: "progressPercentage",
      label: t("team.fields.progressPercentage"),
      type: "number",
      hint: t("team.hints.progressPercentage"),
      visibleWhen: whenMode("project"),
    },
    // "Ya liquidado" NO se teclea: el servidor lo calcula acumulando las
    // liquidaciones anteriores de la misma tarifa (justo para que el mismo
    // avance no se pague dos veces) e ignora lo que mande el cliente. Como
    // campo editable sólo servía para que alguien tecleara una cifra sin
    // ningún efecto. Se muestra, ya calculado, en la vista previa del pago.
    {
      key: "periods",
      label: t("team.fields.periods"),
      type: "number",
      hint: t("team.hints.periods"),
      visibleWhen: whenMode("fixed_salary"),
    },
    { key: "manualBonus", label: t("team.fields.manualBonus"), type: "number" },
    {
      key: "projectId",
      label: t("team.fields.project"),
      hint: t("team.hints.project"),
    },
    {
      key: "costCenterId",
      label: t("team.fields.costCenter"),
      hint: t("team.hints.costCenter"),
    },
    {
      key: "status",
      label: t("team.fields.status"),
      type: "select",
      defaultValue: "calculated",
      options: SETTLEMENT_STATUS_OPTIONS.map((e) => ({
        value: e,
        label: settlementStatusLabel(t, e),
      })),
    },
    { key: "notes", label: t("team.fields.notes"), type: "textarea" },
  ];

  // Vista previa en vivo del cálculo del pago dentro del modal (AgroCloud#13).
  const formFooter = (values: Record<string, string>) => {
    const rate = rates.find((x) => x.id === values.payRateId);
    if (!rate) {
      return (
        <p className={`text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          {t("team.calc.chooseRate")}
        </p>
      );
    }
    const num = (v: string | undefined) => (v === undefined || v === "" ? undefined : Number(v));
    // Lo ya liquidado es un DATO DERIVADO: la suma de las liquidaciones vigentes
    // de esta misma tarifa, con la misma función pura que usa el servidor. Así
    // la vista previa descuenta exactamente lo que descontará el backend.
    const previouslyPaid =
      rate.mode === "project"
        ? computeProjectPaidSoFar(
            rows as unknown as Parameters<typeof computeProjectPaidSoFar>[0],
            rate.id,
          )
        : 0;
    const payment = computeSettlementPayment(rate, {
      output: num(values.output),
      days: num(values.days),
      hours: num(values.hours),
      progressPercentage: num(values.progressPercentage),
      previouslyPaid,
      periods: num(values.periods),
      manualBonus: num(values.manualBonus),
    });
    // El pago no se puede calcular: se dice por qué. Mostrar "MXN 0" aquí haría
    // creer que la tarea no paga nada, y el backend además rechazará el guardado.
    if (payment.noCalcReason) {
      return (
        <p className={`text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          {t(`team.calc.noCalc.${payment.noCalcReason}`)}
        </p>
      );
    }
    return (
      <div className={`rounded-xl border p-3 ${tc.border} ${tc.cardBg}`}>
        <p className={`mb-2 text-xs font-semibold ${tc.text}`}>
          <i className="ri-calculator-line mr-1" />
          {t("team.calc.title")}
        </p>
        <div className="flex items-center justify-between text-xs">
          <span className={tc.textSecondary}>{t("team.calc.appliedPrice")}</span>
          <span className={tc.text}>{formatMoney(payment.appliedUnitPrice)}</span>
        </div>
        <div className="mt-1 flex items-center justify-between text-xs">
          <span className={tc.textSecondary}>{t("team.calc.subtotal")}</span>
          <span className={tc.text}>{formatMoney(payment.subtotal)}</span>
        </div>
        {/* Sólo por proyecto: lo que el servidor ya descontó, de sólo lectura. */}
        {rate.mode === "project" && (
          <div className="mt-1 flex items-start justify-between gap-3 text-xs">
            <span className={tc.textSecondary}>
              {t("team.calc.previouslyPaid")}
              <span className="mt-0.5 block text-[10px] opacity-80">
                {t("team.calc.previouslyPaidHint")}
              </span>
            </span>
            <span className={tc.text}>{formatMoney(previouslyPaid)}</span>
          </div>
        )}
        <div className="mt-1 flex items-center justify-between text-xs">
          <span className={tc.textSecondary}>{t("team.calc.bonus")}</span>
          <span className={tc.text}>{formatMoney(payment.bonus)}</span>
        </div>
        <div className="mt-2 flex items-center justify-between border-t pt-2 text-sm font-semibold dark:border-dark-border">
          <span className={tc.text}>{t("team.calc.total")}</span>
          <span className="text-primary-500">{formatMoney(payment.total)}</span>
        </div>
      </div>
    );
  };

  const toPayload = (values: Record<string, string>): Partial<WorkSettlement> => {
    const subject = (values.subject || "team_member") as WorkSettlement["subject"];
    const rate = rates.find((x) => x.id === values.payRateId);
    return {
      subject,
      teamMemberId: subject === "team_member" ? values.teamMemberId || null : null,
      squadId: subject === "squad" ? values.squadId || null : null,
      payRateId: values.payRateId,
      mode: rate?.mode,
      date: values.date || null,
      output: values.output ? Number(values.output) : null,
      days: values.days ? Number(values.days) : null,
      hours: values.hours ? Number(values.hours) : null,
      progressPercentage: values.progressPercentage ? Number(values.progressPercentage) : null,
      periods: values.periods ? Number(values.periods) : null,
      manualBonus: values.manualBonus ? Number(values.manualBonus) : null,
      projectId: values.projectId || null,
      costCenterId: values.costCenterId || null,
      status: (values.status || "calculated") as WorkSettlement["status"],
      notes: values.notes || null,
    };
  };

  return (
    <ApiCrudSection
      readOnly={!isStaffAdmin}
      title={t("team.menu.settlements")}
      subtitle={t("team.settlementsSubtitle")}
      icon="ri-money-dollar-circle-line"
      columns={columns}
      fields={fields}
      rows={rows}
      loading={loading}
      error={error}
      searchKey="payRateName"
      addLabel={t("team.newSettlement")}
      formFooter={formFooter}
      onCreate={async (values) => {
        await createSettlement(toPayload(values));
        await load();
      }}
      onUpdate={async (id, values) => {
        await updateSettlement(id, toPayload(values));
        await load();
      }}
      onDelete={async (id) => {
        await deleteSettlement(id);
        await load();
      }}
    />
  );
}
